import { useCallback, useEffect, useState } from "react";
import type { PosQuickInput, PosQuickSettingsDto, QuickMethod } from "@delta/shared";
import { useLang } from "../../i18n";
import { api } from "../../lib/api";
import { errorText, fieldErrorsOf } from "../../lib/errors";
import { Icon } from "../../components/Icon";
import { PageHead, Shell } from "../../components/Shell";
import { useToast } from "../../components/Toast";

// Settings › POS payment buttons (rule W-16, migration 032): for each branch, the treasury that the register's Cash and
// mada / Network buttons complete the sale with. Nothing is chosen automatically; a button left unset is disabled at
// the register with the reason. Each choice is saved at once and audited (pos.quickMethod). A treasury that cannot take
// payments yet (no ledger account) is listed, disabled, with the reason; a commission rate is shown beside its name.

const SHIFT = "shift";

export function PosPaymentButtons() {
  const { t, L, lang } = useLang();
  const toast = useToast();
  const [x, setX] = useState<PosQuickSettingsDto | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [errs, setErrs] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setFailed(null);
    try { setX(await api<PosQuickSettingsDto>("GET", "/settings/pos-quick-pay")); } catch (e) { setFailed(errorText(t, e)); }
  }, [t]);
  useEffect(() => { void load(); }, [load]);
  const when = (iso: string) => new Date(iso).toLocaleString(lang === "ar" ? "ar-SA-u-nu-latn-ca-gregory" : "en-GB", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Riyadh" });

  async function save(branchId: string, branchName: string, method: QuickMethod, value: string) {
    const key = `${branchId}.${method}`;
    const body: PosQuickInput = value === SHIFT ? { treasuryId: null, useShiftDrawer: true } : { treasuryId: value || null };
    setBusy(key); setErrs((o) => ({ ...o, [key]: "" }));
    try {
      setX(await api<PosQuickSettingsDto>("PUT", `/settings/pos-quick-pay/${branchId}/${method}`, body));
      const b = t(method === "cash" ? "qpCash" : "qpCard");
      toast(value ? t("qpSaved", { b, x: branchName }) : t("qpCleared", { b, x: branchName }));
    } catch (e) {
      const f = fieldErrorsOf(t, e);
      setErrs((o) => ({ ...o, [key]: Object.values(f)[0] ?? errorText(t, e) }));
    } finally { setBusy(null); }
  }

  const body = failed ? (
    <section className="card"><div className="state-box error"><Icon name="alert" /><h3>{t("loadErrorTitle")}</h3><p>{failed}</p><button className="btn btn-primary" onClick={() => void load()}><Icon name="refresh" />{t("retry")}</button></div></section>
  ) : !x ? <section className="card"><div className="state-box"><p>{t("loading")}</p></div></section> : (
    <div className="set-stack">
      <div className="dep-note" data-testid="qp-intro"><Icon name="info" /><span>{t("qpSettingsIntro")} {t("qpCardNote")}</span></div>
      {x.branches.map(({ branch, cash, card }) => (
        <section className="set-sec" key={branch.id} data-testid="qp-branch" data-branch={branch.nameEn}>
          <div className="set-sec-h"><h2>{L(branch)}</h2></div>
          <div className="card set-sec-b"><div className="form-grid">
            {([["cash", cash], ["card", card]] as const).map(([m, cur]) => {
              const key = `${branch.id}.${m}`;
              const kind = m === "cash" ? "cash" : "bank";
              const choices = x.treasuries.filter((tr) => tr.branchId === branch.id && tr.kind === kind);
              const value = cur.useShiftDrawer ? SHIFT : cur.treasury?.id ?? "";
              const problem = cur.problem && cur.problem !== "not_configured" ? t(`qpProblem_${cur.problem}`, { b: t(m === "cash" ? "qpCash" : "qpCard"), t: cur.treasury ? L(cur.treasury) : "" }) : null;
              return (
                <div className={`field span-2 ${errs[key] || problem ? "invalid" : ""}`} key={m}>
                  <label htmlFor={`qp-${key}`}><Icon name={m === "cash" ? "cash" : "card"} /> {t(m === "cash" ? "qpCash" : "qpCard")}</label>
                  <select id={`qp-${key}`} className="control" value={value} disabled={busy === key} onChange={(e) => void save(branch.id, L(branch), m, e.target.value)} data-testid={`qp-${m}`}>
                    <option value="">{t("qpNotSet")}</option>
                    {m === "cash" && <option value={SHIFT}>{t("qpShiftDrawer")}</option>}
                    {choices.map((tr) => {
                      const why = !tr.hasAccount ? t("qpNoAccountTag") : null;
                      return <option key={tr.id} value={tr.id} disabled={!!why && tr.id !== value}>
                        {L(tr)}{/[1-9]/.test(tr.commission) ? ` · ${t("qpCommission", { x: tr.commission })}` : ""}{why ? ` (${why})` : ""}</option>;
                    })}
                    {cur.treasury && !choices.some((c) => c.id === cur.treasury!.id) && <option value={cur.treasury.id}>{L(cur.treasury)}</option>}
                  </select>
                  <div className="ferr" role="alert" data-testid={`qp-${m}-error`}>{errs[key] || problem}</div>
                  {cur.updatedAt && <span className="hint">{t("qpUpdated", { at: when(cur.updatedAt), u: cur.updatedBy ?? "–" })}</span>}
                </div>
              );
            })}
          </div></div>
        </section>
      ))}
    </div>
  );

  return (
    <Shell navKey="/settings/pos-payment-buttons">
      <PageHead crumbs={[[t("home"), "/home"], [t("settings")], [t("setPosButtons")]]} title={t("setPosButtons")} />
      {body}
    </Shell>
  );
}
